<script setup lang="ts">
import { useUserQuoteProcess } from '@/helpers/useUserQuoteProcess';
import type { ApplicantId } from '@/types';
import { ref, watch } from 'vue';

export interface ApplicantPropertyShare {
  id: ApplicantId;
  share?: number;
}

const { loanApplicants } = useUserQuoteProcess();

const props = defineProps<{
  modelValue: Record<ApplicantId, number>;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', v: Record<ApplicantId, number>): void;
}>();

const applicants = ref<ApplicantPropertyShare[]>([]);

const checkInput = (id: number) => {
  const share = applicants.value[id].share ?? 0;
  if (share > 100) {
    applicants.value[id].share = 100;
  }

  const total = applicants.value.reduce((sum, item) => sum + (item.share ?? 0), 0);

  if (total > 100 && applicants.value.length > 1) {
    const currentShare = applicants.value[id].share ?? 0;
    const remaining = 100 - currentShare;
    const othersTotal = applicants.value.reduce((sum, item, i) => sum + (i !== id ? item.share ?? 0 : 0), 0);

    if (othersTotal > 0) {
      let allocated = 0;
      const otherIndices = applicants.value.map((_, i) => i).filter((i) => i !== id);

      for (let i = 0; i < otherIndices.length; i++) {
        const idx = otherIndices[i];
        const otherShare = applicants.value[idx].share ?? 0;

        if (i === otherIndices.length - 1) {
          // Last one gets whatever is left (handles rounding)
          applicants.value[idx].share = remaining - allocated;
        } else {
          const scaled = Math.floor((otherShare / othersTotal) * remaining);
          applicants.value[idx].share = scaled;
          allocated += scaled;
        }
      }
    } else {
      // All others are 0 — just cap current to 100
      applicants.value[id].share = 100;
    }
  }

  const newValue = applicants.value.reduce((obj, item) => {
    obj[item.id] = item.share ?? 0;
    return obj;
  }, {} as Record<ApplicantId, number>);
  emit('update:modelValue', newValue);
};

const onlyNumbers = (event: KeyboardEvent) => {
  if (event.key < '0' || event.key > '9') {
    event.preventDefault();
  }
};

const getApplicantLabel = (applicantId: ApplicantId): string => {
  const applicant = loanApplicants.value?.[applicantId];
  if (!applicant) return '';
  const fullName = `${applicant.firstName || ''} ${applicant.lastName || ''}`.trim();
  return fullName || applicant.preferredName || '';
};

watch(
  () => props.modelValue,
  (val) => {
    applicants.value = Object.entries(val).map(([id, share]) => ({ id, share }));
  },
  { immediate: true },
);
</script>

<template>
  <div class="flex w-full gap-4">
    <div v-for="(applicant, index) in applicants" :key="applicant.id">
      <BxInput
        :model-value="applicant.share?.toString()"
        :label="getApplicantLabel(applicant.id)"
        label-type="floating"
        :maxlength="applicant.share?.toString().startsWith('1') ? '3' : '2'"
        @update:model-value="applicant.share = $event === '' ? undefined : Number($event)"
        @keypress="onlyNumbers"
        @blur="checkInput(index)"
      >
        <template #trailing>
          <span class="mx-4 text-gray-400">%</span>
        </template>
      </BxInput>
    </div>
  </div>
</template>
